// Regole di livello e di poteri (piano M1a D4, D11; piano M1b D5; decisioni del product owner del 30 settembre
// 2026 sulla questione 38), pure e in una sola copia per server e interfaccia:
// - il proprietario protetto è intangibile per tutti, anche per la piattaforma;
// - nessuno agisce su sé stesso;
// - si agisce, si assegna e si gestisce soltanto sotto il proprio livello, salvo il ruolo di sistema e la
//   piattaforma;
// - il ruolo di sistema non si assegna mai, nemmeno dal Super admin o dalla piattaforma: il Super admin è uno
//   solo, il titolare, e lo riceve soltanto con l'invito della creazione dell'azienda;
// - per lo stesso motivo il ruolo di sistema, o un ruolo del suo livello, non si copia e non si assegna, per
//   nessuno (`isSuperAdminRole`): sarebbe un secondo Super admin di fatto;
// - nessuno dà ad altri più di quello che ha: un ruolo si crea, si modifica e si assegna soltanto con poteri
//   compresi nei propri (`withinPowers`), salvo il ruolo di sistema e la piattaforma.
// Il server resta l'autorità (`apps/server/src/authorization.ts` le applica); l'interfaccia le usa soltanto per
// decidere cosa mostrare.
export function canActOn(actor, target) {
    if (target.isProtectedOwner)
        return { ok: false, code: "protected_owner" };
    if (actor.kind === "platform")
        return { ok: true };
    if (actor.membershipId === target.membershipId)
        return { ok: false, code: "cannot_act_on_self" };
    if (actor.isSystem)
        return { ok: true };
    return target.roleLevel < actor.level ? { ok: true } : { ok: false, code: "role_level" };
}
/**
 * Livello del Super admin. Un ruolo di questo livello è un Super admin di fatto anche quando non è quello di
 * sistema (per esempio una copia scritta prima del 30 settembre 2026).
 */
export const SUPER_ADMIN_LEVEL = 4;
/**
 * Il ruolo di sistema, o un ruolo del suo livello: nessuno lo dà ad altri né lo copia, nemmeno il Super admin o
 * la piattaforma, perché il Super admin è uno solo (questione 38). `isSystem` può mancare dove si conosce
 * soltanto il livello (il riassunto del ruolo nel Team): il ruolo di sistema è comunque di questo livello.
 */
export function isSuperAdminRole(role) {
    return role.isSystem === true || role.level >= SUPER_ADMIN_LEVEL;
}
/**
 * La regola di livello per assegnare un ruolo: il ruolo di sistema, o un ruolo del suo livello, mai, per nessuno
 * (`isSuperAdminRole`, questione 38); ogni altro ruolo per il ruolo di sistema e la piattaforma; per gli altri
 * soltanto un livello inferiore al proprio. I poteri li aggiunge `canGrantRole`.
 *
 * Attenzione: da sola NON basta a decidere se offrire un ruolo. Un select o un pulsante che dà un ruolo usa
 * `checkRoleAssignment` (livello e poteri insieme), come il server: chiamare soltanto questa offrirebbe ruoli
 * che il server poi rifiuta con `permission_denied`.
 */
export function canAssignRole(actor, role) {
    if (isSuperAdminRole(role))
        return false;
    if (actor.kind === "platform" || actor.isSystem)
        return true;
    return role.level < actor.level;
}
// `create` usa soltanto `none` e `all`: lo stesso ordine vale anche lì.
const SCOPE_RANK = { none: 0, own: 1, all: 2 };
/** Soltanto chiavi proprie: mai `constructor` & co. del prototipo. */
function ownValue(record, key) {
    return record && Object.hasOwn(record, key) ? record[key] : undefined;
}
/** Il rango dell'ambito su un'area e un'azione (assente: `none`), `null` se non è un ambito. */
function rankOf(powers, area, action) {
    const scope = ownValue(ownValue(powers.areaPermissions, area), action) ?? "none";
    return typeof scope === "string" && Object.hasOwn(SCOPE_RANK, scope) ? SCOPE_RANK[scope] : null;
}
/**
 * Se `role` sta dentro `powers`: ogni sua funzione è anche di `powers` e ogni suo ambito, area per area e azione
 * per azione, non è più ampio (`none` < `own` < `all`). Si confrontano le aree e le azioni scritte da una parte o
 * dall'altra: una assente vale `none`, un ambito che non si riconosce, da una parte o dall'altra, non passa mai.
 * Vale per il ruolo creato o modificato (M1a Task 11) e per il ruolo assegnato (questione 38); il ruolo di
 * sistema e la piattaforma, che non hanno questo limite, li esclude chi chiama.
 */
export function withinPowers(role, powers) {
    const features = new Set(powers.features);
    if (!role.features.every((feature) => features.has(feature)))
        return false;
    const areas = new Set([...Object.keys(role.areaPermissions), ...Object.keys(powers.areaPermissions)]);
    for (const area of areas) {
        const actions = new Set([...Object.keys(ownValue(role.areaPermissions, area) ?? {}), ...Object.keys(ownValue(powers.areaPermissions, area) ?? {})]);
        for (const action of actions) {
            const given = rankOf(role, area, action);
            const held = rankOf(powers, area, action);
            if (given === null || held === null || given > held)
                return false;
        }
    }
    return true;
}
/**
 * La regola dei poteri per dare un ruolo (invito, reinvio, cambio di ruolo): dentro quelli di chi agisce
 * (`withinPowers`), salvo il ruolo di sistema e la piattaforma. Si aggiunge alla regola di livello
 * (`canAssignRole`), non la sostituisce.
 */
export function canGrantRole(actor, actorPowers, role) {
    if (actor.kind === "platform" || actor.isSystem)
        return true;
    return withinPowers(role, actorPowers);
}
/**
 * Assegnare un ruolo, con il motivo del rifiuto: il ruolo di sistema, o uno del suo livello, mai
 * (`system_role`), poi il livello (`role_level`), poi i poteri (`permission_denied`). Il livello viene prima dei
 * poteri: un ruolo del proprio livello è `role_level` anche con i poteri compresi nei propri.
 */
export function checkRoleAssignment(actor, actorPowers, role) {
    if (isSuperAdminRole(role))
        return { ok: false, code: "system_role" };
    if (!canAssignRole(actor, role))
        return { ok: false, code: "role_level" };
    return canGrantRole(actor, actorPowers, role) ? { ok: true } : { ok: false, code: "permission_denied" };
}
/**
 * Annullare un invito in attesa non dà poteri a nessuno: conta soltanto il livello del suo ruolo, inferiore al
 * proprio, salvo il ruolo di sistema e la piattaforma. L'invito del proprietario protetto non si annulla mai: lo
 * controlla chi chiama.
 */
export function canCancelInvitation(actor, role) {
    if (actor.kind === "platform" || actor.isSystem)
        return true;
    return !role.isSystem && role.level < actor.level;
}
/**
 * Si copia, si modifica o si archivia soltanto un ruolo di livello inferiore al proprio (R16), salvo il ruolo
 * di sistema e la piattaforma: così nessuno allarga il proprio ruolo né uno pari o superiore. Che il ruolo di
 * sistema stesso non si modifichi è una regola a parte, che vale per tutti.
 */
export function canManageRole(actor, role) {
    if (actor.kind === "platform" || actor.isSystem)
        return true;
    return role.level < actor.level;
}
/**
 * «Guarda come…» (decisione del product owner del 2 ottobre 2026): se chi guarda può vedere il programma come lo vede
 * questa persona. La regola contro l'allargamento dei poteri (proposta di Claude, questione aperta 118): il menu della
 * vista non offre mai aree che chi guarda non può aprire, quindi si guarda soltanto chi non ha più poteri di chi guarda.
 * - il Super admin di Gioia365 (la piattaforma, entrato con «Entra») guarda chiunque;
 * - il Super admin dell'azienda (il ruolo di sistema) chiunque tranne sé stesso;
 * - ogni altro soltanto chi ha funzioni e ambiti compresi nei suoi (`withinPowers`), mai il ruolo di sistema, che ha ogni
 *   potere, anche quelli che nessun altro ruolo può avere; mai sé stesso.
 * Si decide sui poteri, non sui nomi né sui livelli, così vale anche per i ruoli su misura. Le funzioni di `viewerPowers`
 * e di `target` sono quelle effettive (le disponibili alle aziende). Che la persona sia della stessa azienda, attiva, e
 * che chi guarda abbia la funzione `commercial_view` lo controlla chi chiama.
 */
export function canObserve(viewer, viewerPowers, target) {
    if (viewer.kind === "platform")
        return true;
    if (viewer.membershipId === target.membershipId)
        return false;
    if (viewer.isSystem)
        return true;
    if (target.isSystem)
        return false;
    return withinPowers(target, viewerPowers);
}
/**
 * Copiare un ruolo in uno su misura («Nuovo ruolo», «Duplica», «Ruolo su misura», `POST /api/roles`): mai il
 * ruolo di sistema né un ruolo del suo livello (`isSuperAdminRole`), per nessuno; poi un livello inferiore al
 * proprio (`canManageRole`) e poteri dentro i propri (`canGrantRole`), salvo il ruolo di sistema e la
 * piattaforma. Il server applica le stesse regole, nell'ordine `system_role`, `role_level`, `permission_denied`.
 */
export function canCopyRole(actor, actorPowers, role) {
    return !isSuperAdminRole(role) && canManageRole(actor, role) && canGrantRole(actor, actorPowers, role);
}
//# sourceMappingURL=access.js.map